import React from 'react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

interface HeroCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  isActive: boolean;
  onToggle: (value: boolean) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const HeroCard: React.FC<HeroCardProps> = ({
  icon,
  title,
  description,
  isActive,
  onToggle,
  disabled = false,
  className,
  children,
}) => {
  return (
    <div
      className={cn(
        // Base layout - PRD §1
        "flex items-center gap-3",
        // Container specs - PRD §2
        "rounded-[12px] p-4",
        "border",
        // Flutua sobre o fundo cinza do painel, como as demais seções
        "shadow-[0_2px_8px_rgba(15,23,42,0.05)]",
        // Ativo ganha fundo azulado: sinaliza o recurso ligado sem depender só do switch
        isActive ? "bg-[#F5F8FF] border-[#C7D7FF]" : "bg-white border-[#E5E7EB]",
        "transition-all duration-200",
        // Hover sutil - PRD §6
        "hover:shadow-[0_4px_14px_rgba(15,23,42,0.07)]",
        !isActive && "hover:border-[#D0D5DD]",
        // Disabled
        disabled && "opacity-50 pointer-events-none",
        className
      )}
    >
      {/* Icon Wrapper - PRD §3 */}
      <div
        className={cn(
          "flex-shrink-0 w-9 h-9",
          "rounded-[10px]",
          "flex items-center justify-center",
          "text-[#1D4FFF]",
          isActive ? "bg-[#E0EAFF]" : "bg-[#EEF4FF]"
        )}
      >
        <div className="w-5 h-5">
          {icon}
        </div>
      </div>

      {/* Content - PRD §4 */}
      <div className="flex-1 min-w-0">
        <h4 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight truncate">
          {title}
        </h4>
        <p className="text-[12px] font-normal text-[#6B7280] leading-snug mt-1 line-clamp-2">
          {description}
        </p>
      </div>

      {/* Toggle - PRD §5 */}
      <Switch
        checked={isActive}
        onCheckedChange={onToggle}
        disabled={disabled}
        className="flex-shrink-0 data-[state=checked]:bg-[#1D4FFF]"
      />

      {/* Optional children content */}
      {children}
    </div>
  );
};
